import type { FunctionComponent } from 'react';
import { useSelector } from 'react-redux';

import { config } from '../../../../config/config';
import { FormattedMessage } from '../../../shared/components/formatted-message/formatted-message';
import { Loader } from '../../../shared/components/loader/loader';
import { Thumbnail } from '../../../shared/components/thumbnail/thumbnail';
import { useDemographicsFilter } from '../../../shared/hooks/demographic';
import { selectLocale } from '../../../shared/store/locale.store';
import { createClassName, createStyleHelper } from '../../../shared/utils/class-names';
import { useGetSimilarGamesQuery } from '../../queries/gameplay.graphql';

import styles from './similar-games.module.scss';

const style = createStyleHelper(styles, 'similar-games');

export interface ISimilarGamesProps {
    className?: string;
    gameSlugs: Array<string>;
}

export const SimilarGames: FunctionComponent<ISimilarGamesProps> = ({ className, gameSlugs }) => {
    const SIMILAR_GAMES_AMOUNT = 6;

    const locale = useSelector(selectLocale);
    const [{ fetching: loadingGames, data: games }] = useGetSimilarGamesQuery({
        variables: {
            xTenantId: config.TENANT_ID,
            skinId: config.SKIN_ID,
            locale,
            slugs: gameSlugs,
        },
    });

    const similarGames = useDemographicsFilter(games?.getSimilarGames ?? []).slice(
        0,
        SIMILAR_GAMES_AMOUNT,
    );

    if (loadingGames || !games) {
        return <Loader />;
    }

    if (!similarGames || similarGames.length === 0) {
        return null;
    }

    return (
        <div className={createClassName([className, style()])}>
            <h2 className={style('text')}>
                <FormattedMessage
                    id="gameplay.similar-games.similar-games"
                    defaultMessage="Similar games"
                />
            </h2>
            {similarGames.map((game) => {
                return (
                    <Thumbnail
                        className={style('game')}
                        key={game.id}
                        slug={game.slug}
                        tags={game.gameTags}
                        background={game.thumbnails[0]?.background || ''}
                        logo={game.thumbnails[0]?.logo}
                        asset={game.thumbnails[0]?.asset}
                        variant={game.thumbnails[0]?.layout?.variant}
                    />
                );
            })}
        </div>
    );
};
